import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createRole,
  deleteRole,
  duplicateRole,
  getAvailablePermissions,
  getRoleDetailById,
  getRoleList,
  getUserPermissions,
  updateRole,
} from '~apis/role';
import { useCompanyId } from '~hooks/useCompanyId';
import { useUserId } from '~hooks/useUserId';
import { queryClient } from '~libs/queryClient';

import { CompaniesQueryKey } from '../companies/query';

export const RoleQueryKey = {
  default: 'roles',
  list: (companyId: string) => [RoleQueryKey.default, companyId],
  detail: (companyId: string, roleId: number) => [RoleQueryKey.default, companyId, roleId],
  availablePermissions: (companyId: string) => [
    RoleQueryKey.default,
    companyId,
    'available-permissions',
  ],
  userPermissions: (companyId: string, userId: string) => [
    RoleQueryKey.default,
    companyId,
    userId,
    'user-permissions',
  ],
};

export const useRoleList = (companyId: string) => {
  return useQuery({
    queryKey: RoleQueryKey.list(companyId),
    queryFn: () => getRoleList(companyId),
    enabled: !!companyId,
  });
};

export const useAvailablePermissions = (companyId: string) => {
  return useQuery({
    queryKey: RoleQueryKey.availablePermissions(companyId),
    queryFn: () => getAvailablePermissions(companyId),
    enabled: !!companyId,
  });
};
export const useRoleDetail = (companyId: string, roleId: number) => {
  return useQuery({
    queryKey: RoleQueryKey.detail(companyId, roleId),
    queryFn: () => getRoleDetailById(companyId, roleId),
    enabled: !!companyId && !!roleId,
  });
};

export const useUserPermissions = () => {
  const userId = useUserId();
  const { companyId } = useCompanyId();
  return useQuery({
    queryKey: RoleQueryKey.userPermissions(companyId, userId),
    queryFn: () => getUserPermissions({ companyId, userId }),
    enabled: !!companyId && !!userId,
  });
};

export const useCreateRole = () => {
  return useMutation({
    mutationFn: createRole,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: RoleQueryKey.list(variables.companyId) });
      queryClient.invalidateQueries({
        queryKey: CompaniesQueryKey.members({ companyId: variables.companyId }),
      });
    },
  });
};

export const useDuplicateRole = () => {
  return useMutation({
    mutationFn: duplicateRole,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: RoleQueryKey.list(variables.companyId) });
      queryClient.invalidateQueries({
        queryKey: CompaniesQueryKey.members({ companyId: variables.companyId }),
      });
    },
  });
};

export const useDeleteRole = () => {
  return useMutation({
    mutationFn: deleteRole,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: RoleQueryKey.list(variables.companyId) });
      queryClient.invalidateQueries({
        queryKey: CompaniesQueryKey.members({ companyId: variables.companyId }),
      });
    },
  });
};

export const useUpdateRole = () => {
  return useMutation({
    mutationFn: updateRole,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: RoleQueryKey.list(variables.companyId) });
      queryClient.invalidateQueries({
        queryKey: RoleQueryKey.detail(variables.companyId, variables.roleId),
      });
      queryClient.invalidateQueries({
        queryKey: CompaniesQueryKey.members({ companyId: variables.companyId }),
      });
    },
  });
};
